import React, { useEffect, useMemo, useState } from 'react';
import { T } from './speedTheme';
import StatRuneSwitch from './StatRuneSwitch';
import RuneBuildPanel from './RuneBuildPanel';
import MonsterStatPanel from './MonsterStatPanel';
import { runesFromAccount, unitsFromAccount, statsWith } from './runeEngine';
import { artsOfRaw, mergeArts, setsText, TacticBuild } from './TacticBuild';
import { FitTarget } from './SimMemory';
import { loadRawAccount, RawAccount } from '../utils/runeStore';
import { RuneFitSource } from '../utils/fitHandoff';
import { getMonsterImgPath } from '../utils/monster';
import { mobImgUrl } from '../utils/monsterCells';
import RuneAdvanced, { AdvancedPreset, StatKey } from './RuneAdvanced';

/**
 * **룬피팅 탭** (룬 옵티마이저, 사용자 2026-10-27 "전투력 탭 왼쪽에 룬피팅 탭") — 전투기록 창·공략 [룬 정보]의 [내 룬으로 맞추기]로 넘어온
 * 공덱 룬작(utils/fitHandoff)에 **내 룬·아티**를 맞춘다. 공덱 몹(1~3)을 한꺼번에, 룬이 겹치지 않게, 리더 칸부터 (사용자 고름 2026-10-27).
 *
 * 목표는 원본 룬작에서 — 세트는 그대로, 공속·치확·효적·효저는 원본 숫자 이상(문턱), 나머지(체·공·방·치피)는 몹 유형별 가중으로 최대한.
 * 아티는 원본 아티 부옵에 가까운 것. 몹은 게임 자료 맨몸(6성 40) 그대로 — 보유몹은 보지 않는다.
 * 셈은 서버(POST /api/sim/fit → swSim engine/src/accountfit.ts, C++ 룬 옵티마이저 fitDeck). 시뮬 탭 [내 룬 자동할당]과 같은 길이다.
 * 계정은 이 탭에서 불러온 JSON(원본은 utils/runeStore gaRaw) — 맞출 때만 서버로 보내고 서버는 저장하지 않는다.
 */
type GateKey = 'spd' | 'cr' | 'acc' | 'res';
type Gates = Record<GateKey, boolean>;
interface FitMob {
    found: boolean; raw?: any; target?: FitTarget; short?: { spd: number; cr: number; acc: number; res: number }; setsMissing?: number[];
    /** 쓴 문턱 · 원본 숫자 — 서버가 알아서 고른다(engine accountfit gatesAuto). 룬 고급 설정의 처음 값이 된다 */
    gates?: Gates; wanted?: Record<GateKey, number>;
}

/** 원본 룬작 → 서버가 목표를 만드는 능력치 (engine accountfit targetFromStats 의 이름) */
const statsOf = (b: TacticBuild | null | undefined) => (b ? { hp: b.hp, atk: b.atk, def: b.def, spd: b.spd, cri_rate: b.cr, cri_dmg: b.cd, res: b.res, acc: b.acc } : {});
const artOf = (b: TacticBuild | null | undefined): Record<number, number> => (b?.raw ? mergeArts(artsOfRaw(b.raw)) : b?.arts ? mergeArts(b.arts) : {});

/** 서버로 보낼 계정 — 룬·아티 창고만 (보관함 + 모든 몹이 낀 것) */
const accountPayload = (a: RawAccount) => ({
    // 몹은 번호·새긴 유물만 — 같은 몹이 있으면 그 유물을 쓴다(룬·아티는 아래 창고로 따로)
    units: a.units.map((u) => ({ unit_id: u.unit_id, unit_master_id: u.unit_master_id, unit_level: u.unit_level, class: u.class, runes: [], artifacts: [], relics: u.relics || [] })),
    relics: [...(a.relics || []), ...a.units.flatMap((u) => u.relics || [])],
    runes: [...a.runes, ...a.units.flatMap((u) => u.runes || [])],
    artifacts: [...(a.artifacts || []), ...a.units.flatMap((u) => u.artifacts || [])]
});

/** 맞춘 몹 원본 → 옵티마이저 그림 재료 (BuildRunes 와 같은 셈) */
function drawOf(raw: any) {
    try {
        const account = { unit_list: [{ ...raw, unit_id: 1 }] };
        const unit = unitsFromAccount(account)[0] || null;
        const runes = runesFromAccount(account).runes.sort((a, b) => a.slot - b.slot);
        return unit ? { unit, runes } : null;
    } catch {
        return null;
    }
}

/** 목표에 모자란 것 — 한 줄 */
/**
 * '원본에 가깝게' 목표에서 크게 벗어난 것 — 체력·공격·방어는 8% 넘게 모자라면, 치피는 20%p 넘게 벗어나면
 * (사용자 2026-10-28 "목표를 못채웠는데도 '목표 다 채움'이 떠. 너무 차이가 날 경우 표시해줘야지" — 문턱(공속·효적…)만 봤다).
 * 셈은 몹 창과 같은 능력치(아티팩트·유물 주옵 포함).
 */
function nearGaps(m: FitMob, draw: ReturnType<typeof drawOf>): string[] {
    const nr = m.target?.near;
    if (!nr || !draw) return [];
    const lines = statsWith(draw.unit, draw.runes, draw.unit.artifacts);
    const got = (k: string) => { const l = lines.find((x) => x.key === k); return l ? l.base + l.bonus : 0; };
    const out: string[] = [];
    for (const [k, label, want] of [['hp', '체력', nr.hp], ['atk', '공격력', nr.atk], ['def', '방어력', nr.def]] as const) {
        if (!(want > 0)) continue;
        const pct = Math.round(((want - got(k)) / want) * 100);
        if (pct > 8) out.push(`${label} ${pct}% 모자람`);
    }
    if (nr.cd > 0) {
        const d = Math.round(got('cd') - nr.cd);
        if (Math.abs(d) > 20) out.push(`치명 피해 ${Math.abs(d)} ${d > 0 ? '넘침' : '모자람'}`);
    }
    return out;
}

function shortText(m: FitMob, draw?: ReturnType<typeof drawOf>): string {
    const parts: string[] = [];
    if (m.setsMissing?.length) parts.push(`${setsText(m.setsMissing)} 세트 못 맞춤`);
    const s = m.short;
    if (s) for (const [k, label] of [['spd', '공격속도'], ['cr', '치명 확률'], ['acc', '효과 적중'], ['res', '효과 저항']] as const) if (s[k] > 0) parts.push(`${label} ${Math.ceil(s[k])} 모자람`);
    parts.push(...nearGaps(m, draw ?? null));
    return parts.join(' · ');
}

export default function RuneFit({ source: given, isMobile, monsterMapping }: { source: RuneFitSource | null; isMobile: boolean; monsterMapping: any }) {
    // 빈 칸은 뺀다 — 공덱이 한두 마리여도 그만큼 맞춘다 (사용자 2026-10-27 "공덱 한마리이상이면 그냥 되게해")
    const source = useMemo<RuneFitSource | null>(() => {
        if (!given) return null;
        const keep = given.mobs.map((m, i) => i).filter((i) => given.mobs[i]?.name);
        return { ...given, mobs: keep.map((i) => given.mobs[i]), builds: keep.map((i) => given.builds[i] ?? null) };
    }, [given]);
    const [mobs, setMobs] = useState<FitMob[] | null>(null);
    /** 서버가 찾은 몹 번호(공략은 이름·속성뿐) · 맞출 때 쓴 계정 — 룬 고급 설정(RuneAdvanced)이 쓴다 */
    const [ids, setIds] = useState<number[]>([]);
    const [account, setAccount] = useState<RawAccount | null>(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [pick, setPick] = useState(0);
    /** 룬 고급 설정 결과가 떠 있나 — 그동안 위 몹 능력치·룬 판을 감춘다([변경 적용]하면 결과가 닫히고 다시 보인다) */
    const [advActive, setAdvActive] = useState(false);
    // 문턱은 처음 맞출 때 서버가 알아서 고른다(engine accountfit gatesAuto). 바꾸는 것은 아래 룬 고급 설정에서 (예전 칩 — 사용자 2026-10-28 뺌)

    useEffect(() => {
        if (!source) return;
        if (!source.mobs.length) { setErr('공덱 몹이 없습니다'); return; }
        let alive = true;
        (async () => {
            setBusy(true); setErr('');
            setMobs(null); setPick(0);
            try {
                const a = await loadRawAccount();
                if (alive) setAccount(a);
                if (!a) throw new Error('계정 JSON 을 한 번 더 불러와 주세요 — 예전에 불러온 계정에는 맞추는 데 쓰는 원본이 없습니다');
                const ids = source.mobs.map((m) => Number(m.unitMasterId) || 0);
                const r = await fetch('/api/sim/fit', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                    body: JSON.stringify({
                        deck: {
                            ...(ids.every((x) => x > 0) ? { units: ids } : { mobs: source.mobs.map((m) => ({ name: m.name, attribute: m.attribute })) }),
                            stats: source.builds.map(statsOf), sets: source.builds.map((b) => b?.sets || []), art: source.builds.map(artOf)
                        },
                        account: accountPayload(a)
                    })
                });
                const d = await r.json();
                if (!d?.success) throw new Error(d?.error || '룬을 맞추지 못했습니다');
                if (!Array.isArray(d.mobs) || d.mobs.length !== source.mobs.length) throw new Error('서버가 결과를 주지 않았습니다');
                if (alive) { setMobs(d.mobs); setIds(Array.isArray(d.units) ? d.units.map(Number) : []); }
            } catch (e: any) {
                if (alive) setErr(e.message || '룬을 맞추지 못했습니다');
            }
            if (alive) setBusy(false);
        })();
        return () => { alive = false; };
    }, [source]);

    /**
     * 룬 고급 설정의 처음 값 = 맞춘 목표 (예전 '목표 폭주+의지 · [공속 271] [치확 15] … 에 가깝게' 칩 줄을 이것으로 바꿨다 — 사용자 2026-10-28).
     * 켠 문턱(서버 gates — 없으면 목표에 숫자가 있는 것)은 '이상'으로 켜고, 체·공·방·치피는 원본 숫자만 채워 둔다.
     */
    const presetOf = (m: FitMob | undefined): AdvancedPreset | null => {
        const tg = m?.target;
        if (!tg) return null;
        const lo: AdvancedPreset['lo'] = { spd: tg.spd || undefined, cr: tg.cr || undefined, acc: tg.acc || undefined, res: tg.res || undefined };
        if (tg.near) { lo.hp = tg.near.hp || undefined; lo.atk = tg.near.atk || undefined; lo.def = tg.near.def || undefined; lo.cd = tg.near.cd || undefined; }
        const on = (['spd', 'cr', 'acc', 'res'] as const).filter((k) => (m!.gates ? !!m!.gates[k] : Number(tg[k]) > 0) && Number(tg[k]) > 0 && Number(tg[k]) < 900);
        // 특별 조항 — 풍드나(바람 드래곤 나이트 16613 · 16603)가 덱에 있으면 공격속도는 조건으로 넣지 않는다 (사용자 2026-10-29, 엔진 accountfit 과 같다)
        const noSpd = ids.some((id) => id === 16613 || id === 16603);
        return { four: tg.four, two: tg.two || [], lo, on: on.filter((k) => !(noSpd && k === 'spd')), ...(noSpd ? { off: ['spd'] as StatKey[] } : {}) };
    };

    // 위 능력치·룬 판은 [변경 적용] 한 룬만 — 결과를 고르기만 해서는 바뀌지 않는다 (사용자 2026-10-28 "변경 적용하기도 전에 룬이 이미 적용돼버리는 문제")
    const draws = useMemo(() => (mobs ? mobs.map((m) => (m.found && m.raw ? drawOf(m.raw) : null)) : null), [mobs]);

    if (!source) {
        return (
            <div style={{ padding: isMobile ? '28px 12px' : '40px 16px', textAlign: 'center', color: T.sub, fontSize: isMobile ? 12.5 : 14, fontWeight: 700, lineHeight: 1.7 }}>
                전투기록 창이나 공략 [룬 정보]에서 <b style={{ color: T.blue }}>[내 룬으로 맞추기]</b>를 누르면<br />
                그 공덱의 룬작에 내 룬·아티를 여기서 맞춥니다
            </div>
        );
    }

    const look = (i: number) => getMonsterImgPath(monsterMapping, source.mobs[i]);
    const sel = mobs?.[pick];
    const draw = draws?.[pick];
    const t = sel?.target;
    return (
        <div style={{ padding: isMobile ? '0 2px' : '0 6px' }}>
            {/* 머리 설명 줄('공략 「…」 공덱 룬작에 내 룬·아티를 맞췄습니다')은 뺐다 — 사용자 2026-10-28. 맞추는 중은 몹 칸에 보인다 */}
            {err && <div style={{ marginBottom: 10, color: T.red, fontSize: isMobile ? 12 : 13, fontWeight: 700 }}>{err}</div>}

            {/* 세 몹 — 드데조 칸과 같은 모양. 누르면 그 몹의 능력치·룬 */}
            <div style={{ display: 'flex', gap: isMobile ? 8 : 10, marginBottom: 12 }}>
                {source.mobs.map((m, i) => {
                    const on = i === pick;
                    const r = mobs?.[i];
                    const short = r ? shortText(r, draws?.[i]) : '';
                    const img = look(i).imgPath;
                    return (
                        <button
                            key={i} type="button" onClick={() => setPick(i)}
                            style={{
                                flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                                padding: isMobile ? '8px 4px' : '10px 6px', borderRadius: 14, cursor: 'pointer',
                                background: on ? 'rgba(102,126,234,0.10)' : '#fff',
                                border: on ? `2px solid ${T.blue}` : '1px solid rgba(102,126,234,0.25)'
                            }}
                        >
                            {img
                                ? <img src={mobImgUrl(img)} alt={look(i).displayName} style={{ width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius: 10, objectFit: 'contain', background: '#f8faff' }} />
                                : <div style={{ width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius: 10, background: '#eee' }} />}
                            <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 900, color: on ? T.blue : '#555', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{look(i).displayName || m.name}</span>
                            <span style={{ fontSize: 10.5, fontWeight: 800, color: !r ? T.sub : short ? T.red : '#2e7d32', textAlign: 'center' }}>
                                {!r ? (busy ? '맞추는 중…' : '—') : short || '목표 다 채움'}
                            </span>
                        </button>
                    );
                })}
            </div>

            {draw && !advActive && (
                <StatRuneSwitch
                    isMobile={isMobile}
                    stat={<MonsterStatPanel unit={draw.unit} runes={draw.runes} artifacts={draw.unit.artifacts} name={look(pick).displayName} img={look(pick).imgPath ? mobImgUrl(look(pick).imgPath) : null} isMobile={isMobile} />}
                    rune={<RuneBuildPanel key={`fit-${pick}`} runes={draw.runes} objective="power" relic={draw.unit.relics[0] || null} artifacts={draw.unit.artifacts} isMobile={isMobile} asIs initialPick={1} />}
                />
            )}

            {/* 룬 고급 설정 — 게임 창처럼 세트·주옵·목표 능력치로 여럿 찾아 고른다 (사용자 2026-10-28). [변경 적용]은 이 몹의 룬만 바꾼다 */}
            {sel?.raw && ids[pick] > 0 && (
                <RuneAdvanced
                    key={`adv-${pick}-${ids[pick]}-${JSON.stringify(t || null)}`}
                    unitMasterId={ids[pick]} current={sel.raw} source={source.builds[pick] || null} preset={presetOf(sel)} account={account}
                    monsterMapping={monsterMapping} isMobile={isMobile}
                    onApply={(raw) => setMobs((cur) => (cur ? cur.map((m, i) => (i === pick ? { ...m, raw } : m)) : cur))}
                    onActive={setAdvActive}
                />
            )}
        </div>
    );
}
